/**
 * «Проверка фибо» — сетки по диапазонам и их исходы на истории.
 *
 * Метод владельца (19.10.2026): берётся хай и лой диапазона, на них
 * натягивается сетка; пробит лой — цель 1.414 снизу, пробит хай — сверху;
 * от зоны 1.414–1.618 можно встать против движения с целью вернуться к 1.0.
 *
 * Здесь — тот же разбор, что в замерах `research/newbots/fibo6.mjs` и
 * `fibo7.mjs`, и общий для графика и оповещений:
 * - диапазон — последний подтверждённый хай и последний подтверждённый лой
 *   (развороты: выше/ниже `swing` свечей с каждой стороны), ширина ≥ 3 ATR;
 * - пробой — первое закрытие за любой границей, пока не подтвердился
 *   следующий разворот (тогда диапазон другой);
 * - сетка: 0 — противоположная граница, 1 — пробитая, уровень x — 0 + x·(1 − 0);
 * - исход пробоя: дошла до 1.414 раньше, чем вернулась к 0.5, — «дошла»;
 *   наоборот — «вернулась»; не дольше `HOLD` свечей;
 * - разворот: после 1.414 — вернулась к 1.0 раньше, чем ушла за 1.628, —
 *   «к 1.0»; наоборот — «стоп».
 * У каждого исхода — шанс случайного графика при тех же расстояниях: так
 * видно, сколько дал уровень, а сколько геометрия сетки (до близкой цели
 * доходят чаще, до далёкой — реже, на любом графике).
 */
/** Размер свингов: сколько свечей с каждой стороны ниже хая (выше лоя). */
export const FIBO_SWINGS = { small: 5, medium: 10, large: 20 };
/** Сколько свечей ждать исхода пробоя и разворота. */
const HOLD = 200;
/** Стоп разворота — чуть дальше 1.618, как у владельца. */
export const FIBO_REVERSE_STOP = 1.628;
/** Шанс случайного графика вернуться от 1.414 к 1.0 раньше, чем уйти за стоп. */
export const FIBO_REVERSE_CHANCE = (FIBO_REVERSE_STOP - 1.414) / (FIBO_REVERSE_STOP - 1.414 + (1.414 - 1));
/** Уровень сетки: 0 — противоположная пробою граница, 1 — пробитая; без пробоя — от лоя к хаю. */
export function fiboLevel(grid, x) {
    const up = grid.broken ? grid.broken.side > 0 : true;
    const zero = up ? grid.low.price : grid.high.price;
    const one = up ? grid.high.price : grid.low.price;
    return zero + x * (one - zero);
}
/** Уровень сетки, пробитой в сторону `side`, — для цели «если пробьют здесь». */
export function fiboTarget(grid, side, x) {
    const zero = side > 0 ? grid.low.price : grid.high.price;
    const one = side > 0 ? grid.high.price : grid.low.price;
    return zero + x * (one - zero);
}
/** Все диапазоны ряда с пробоями и исходами; последний — текущий, может быть ещё не пробит. */
export function fiboGrids(bars, swing) {
    const n = bars.length;
    if (n < swing * 2 + 20)
        return [];
    const atr = [];
    let sum = 0;
    for (let k = 0; k < n; k += 1) {
        const bar = bars[k];
        const prev = bars[k - 1];
        const range = prev ? Math.max(bar.high - bar.low, Math.abs(bar.high - prev.close), Math.abs(bar.low - prev.close)) : bar.high - bar.low;
        sum += range;
        if (k >= 14) {
            const old = bars[k - 14];
            const before = bars[k - 15];
            sum -= before ? Math.max(old.high - old.low, Math.abs(old.high - before.close), Math.abs(old.low - before.close)) : old.high - old.low;
        }
        atr.push(k >= 13 ? sum / 14 : null);
    }
    const pivots = [];
    for (let k = swing; k < n - swing; k += 1) {
        let hi = true;
        let lo = true;
        for (let j = k - swing; j <= k + swing; j += 1) {
            if (j === k)
                continue;
            if (bars[j].high >= bars[k].high)
                hi = false;
            if (bars[j].low <= bars[k].low)
                lo = false;
            if (!hi && !lo)
                break;
        }
        if (hi)
            pivots.push({ k, type: 'H', price: bars[k].high, known: k + swing });
        if (lo)
            pivots.push({ k, type: 'L', price: bars[k].low, known: k + swing });
    }
    pivots.sort((a, b) => a.known - b.known || a.k - b.k);
    const out = [];
    let H = null;
    let L = null;
    for (let p = 0; p < pivots.length; p += 1) {
        const pivot = pivots[p];
        if (pivot.type === 'H')
            H = pivot;
        else
            L = pivot;
        const width = atr[pivot.k];
        if (!H || !L || width === null || width === undefined || H.price - L.price < 3 * width)
            continue;
        const until = p + 1 < pivots.length ? pivots[p + 1].known : n - 1;
        const base = {
            high: { time: bars[H.k].time, price: H.price },
            low: { time: bars[L.k].time, price: L.price },
            knownAt: bars[pivot.known].time,
        };
        let grid = { ...base, broken: null, outcome: 'wait', outcomeAt: null, chance: null, reverse: null, reverseAt: null };
        for (let j = pivot.known + 1; j <= until && j < n; j += 1) {
            const close = bars[j].close;
            const side = close > H.price ? 1 : close < L.price ? -1 : 0;
            if (!side)
                continue;
            grid = settle({ ...grid, broken: { side, at: bars[j].time, close } }, bars, j);
            break;
        }
        // Не пробитый диапазон в истории не нужен: его сменил следующий. Нужен только последний.
        if (grid.broken || p === pivots.length - 1)
            out.push(grid);
    }
    return out;
}
/** Исход пробоя и разворота после свечи пробоя `j`. */
function settle(grid, bars, j) {
    const side = grid.broken.side;
    const close = grid.broken.close;
    const lvl = (x) => fiboLevel(grid, x);
    const target = lvl(1.414);
    const back = lvl(0.5);
    const chance = side * (target - close) <= 0 ? 1 : Math.abs(close - back) / (Math.abs(close - back) + Math.abs(target - close));
    let outcome = 'wait';
    let outcomeAt = null;
    let hitIndex = -1;
    if (chance === 1) {
        outcome = 'hit';
        outcomeAt = bars[j].time;
        hitIndex = j;
    }
    for (let q = j + 1; outcome === 'wait' && q < bars.length && q <= j + HOLD; q += 1) {
        const bar = bars[q];
        if (side > 0 ? bar.high >= target : bar.low <= target) {
            outcome = 'hit';
            outcomeAt = bar.time;
            hitIndex = q;
        }
        else if (side > 0 ? bar.low <= back : bar.high >= back) {
            outcome = 'miss';
            outcomeAt = bar.time;
        }
    }
    let reverse = null;
    let reverseAt = null;
    if (outcome === 'hit') {
        reverse = 'wait';
        const stop = lvl(FIBO_REVERSE_STOP);
        const one = lvl(1);
        for (let q = hitIndex; q < bars.length && q <= hitIndex + HOLD; q += 1) {
            const bar = bars[q];
            // Свеча касания: стоп в ней же — стоп; к 1.0 — только со следующей.
            if (side > 0 ? bar.high >= stop : bar.low <= stop) {
                reverse = 'stop';
                reverseAt = bar.time;
                break;
            }
            if (q > hitIndex && (side > 0 ? bar.low <= one : bar.high >= one)) {
                reverse = 'back';
                reverseAt = bar.time;
                break;
            }
        }
    }
    return { ...grid, outcome, outcomeAt, chance, reverse, reverseAt };
}
/** Счёт по сеткам, пробитым не раньше `since`. */
export function fiboStats(grids, since) {
    const broken = grids.filter((grid) => grid.broken && grid.broken.at >= since);
    const settled = broken.filter((grid) => grid.outcome !== 'wait');
    const hits = settled.filter((grid) => grid.outcome === 'hit');
    const reversals = hits.filter((grid) => grid.reverse === 'back' || grid.reverse === 'stop');
    return {
        settled: settled.length,
        hits: hits.length,
        chance: settled.length ? settled.reduce((s, grid) => s + (grid.chance ?? 0), 0) / settled.length : null,
        reversals: reversals.length,
        backs: reversals.filter((grid) => grid.reverse === 'back').length,
    };
}
//# sourceMappingURL=fibo-check.js.map